import { useQueries } from 'react-query'
import type { HazardInfo } from '../../types/climate'
import type { MetricDataFilters } from '../analytics'
import { portfolioClimateDataQuery } from './index'
import type { AdaptationView, CanonicalPeriod, EmissionTier } from '../../components/climateAnalytics/common'
import {
  HAZARD_AAL_CONFIG,
  extractPropertyAal,
  combinePropertyAal,
  summarizeHazardAal,
  type PortfolioHazardAal,
} from '../../components/climateAnalytics/portfolio/portfolioAal'
import {
  hazardRiskQueryParams,
  isHazardPeriodAvailable,
} from '../../components/climateAnalytics/portfolio/portfolioRisk'

type Args = {
  hazards: HazardInfo[]
  groupId?: number
  propertyIds: number[]
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
  view: AdaptationView
  floodFilters?: MetricDataFilters | null
  enabled: boolean
}

export default function usePortfolioHazardAal({
  hazards,
  groupId,
  propertyIds,
  emissionTier,
  selectedPeriod,
  view,
  floodFilters,
  enabled,
}: Args): PortfolioHazardAal[] {
  const modelledHazards = hazards.filter(hazard => HAZARD_AAL_CONFIG[hazard.name])
  const queries = useQueries(
    modelledHazards.flatMap(hazard => {
      const config = HAZARD_AAL_CONFIG[hazard.name]
      return [config.damageMetric, config.biMetric].map(metric => ({
        ...portfolioClimateDataQuery(
          metric,
          { groupId },
          {
            ...hazardRiskQueryParams(hazard.name, emissionTier, selectedPeriod, view),
            ...(hazard.name === 'fld' ? { filters: floodFilters ?? null } : {}),
          },
        ),
        enabled: enabled && propertyIds.length > 0 && isHazardPeriodAvailable(hazard.name, selectedPeriod),
        keepPreviousData: false,
      }))
    }),
  )

  return hazards.map(hazard => {
    const index = modelledHazards.findIndex(model => model.name === hazard.name)
    const modelled = index >= 0
    const periodAvailable = isHazardPeriodAvailable(hazard.name, selectedPeriod)
    const damageQuery = modelled ? queries[index * 2] : undefined
    const biQuery = modelled ? queries[index * 2 + 1] : undefined
    const error = Boolean(modelled && periodAvailable && (damageQuery?.isError || biQuery?.isError))
    const loading = Boolean(
      modelled &&
        periodAvailable &&
        (!enabled || (propertyIds.length > 0 && (damageQuery?.isLoading || biQuery?.isLoading))),
    )
    const extract = (component: 'damage' | 'bi', query: typeof damageQuery) =>
      Object.fromEntries(
        propertyIds.map(id => [
          id,
          extractPropertyAal(
            hazard.name,
            component,
            periodAvailable && !error && !loading && enabled ? query?.data?.[id] : undefined,
            emissionTier,
            selectedPeriod,
          ),
        ]),
      )
    const damage = extract('damage', damageQuery)
    const bi = extract('bi', biQuery)
    return {
      hazard,
      modelled,
      periodAvailable,
      error,
      loading,
      summary: summarizeHazardAal(propertyIds, damage, bi),
      byProperty: Object.fromEntries(propertyIds.map(id => [id, combinePropertyAal(damage[id], bi[id])])),
      retry: modelled
        ? () => {
            void damageQuery?.refetch()
            void biQuery?.refetch()
          }
        : undefined,
    }
  })
}
